import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../../config/api';

const JobDetailsModal = ({ 
  open, 
  onClose, 
  jobId,
  onActionComplete 
}) => {
  const [job, setJob] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && jobId) {
      fetchJobDetails();
    }
  }, [open, jobId]);

  const fetchJobDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching job details for jobId:', jobId);
      const url = `/api/connectors/sync-jobs/${jobId}`;
      console.log('Making request to URL:', url);
      
      const response = await api.get(url);
      console.log('API Response:', response);
      
      if (response.data.success) {
        console.log('Job data:', response.data.data.job);
        console.log('Logs data:', response.data.data.logs);
        setJob(response.data.data.job);
        setLogs(response.data.data.logs || []);
      } else {
        console.error('API returned success=false:', response.data);
        setError('Failed to load job details: ' + (response.data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error fetching job details:', err);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);
      console.error('Error response status:', err.response?.status);
      setError(err.response?.data?.message || err.message || 'Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const handleJobAction = async (action) => {
    try {
      setLoading(true);
      let response;
      
      if (action === 'cancel') {
        response = await api.post(`/api/connectors/sync-jobs/${jobId}/cancel`);
      } else if (action === 'retry') {
        response = await api.post(`/api/connectors/sync-jobs/${jobId}/retry`);
      }

      if (response?.data.success) {
        onActionComplete?.();
        await fetchJobDetails(); // Refresh the job details
      }
    } catch (err) {
      console.error(`Error ${action}ing job:`, err);
      setError(err.response?.data?.message || `Failed to ${action} job`);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const downloadLogs = () => {
    const logText = logs.map(log =>
      `[${log.timestamp}] ${log.log_level}: ${log.message}${log.details ? '\n  Details: ' + log.details : ''}`
    ).join('\n');

    // Create concise filename: platform_status_YYYYMMDD-HHMM_jobid.txt
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, ''); // YYYYMMDD
    const timeStr = now.toISOString().slice(11, 16).replace(':', ''); // HHMM
    const platformName = (job?.platform_name || 'unknown').toLowerCase().replace(/\s+/g, '');
    const status = (job?.status || 'unknown').toLowerCase();
    const filename = `${platformName}_${status}_${dateStr}-${timeStr}_${jobId}.txt`;

    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getLogLevelIcon = (level) => {
    switch (level?.toUpperCase()) {
      case 'ERROR': return <ErrorIcon color="error" fontSize="small" />;
      case 'WARN': return <WarningIcon color="warning" fontSize="small" />;
      case 'INFO': return <InfoIcon color="info" fontSize="small" />;
      case 'DEBUG': return <InfoIcon color="disabled" fontSize="small" />;
      default: return <InfoIcon color="disabled" fontSize="small" />;
    }
  };

  const getLogLevelColor = (level) => {
    switch (level?.toUpperCase()) {
      case 'ERROR': return 'error';
      case 'WARN': return 'warning';
      case 'INFO': return 'info';
      case 'DEBUG': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <SuccessIcon color="success" />;
      case 'failed': return <ErrorIcon color="error" />;
      case 'running': return <CircularProgress size={20} />;
      default: return <InfoIcon />;
    }
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    const duration = new Date(end) - new Date(start);
    const minutes = Math.floor(duration / (1000 * 60));
    const seconds = Math.floor((duration % (1000 * 60)) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  if (loading && !job) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading job details...
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">
              Sync Job Details
            </Typography>
            {job && (
              <Chip 
                icon={getStatusIcon(job.status)}
                label={job.status?.toUpperCase()}
                color={job.status === 'completed' ? 'success' : 
                       job.status === 'failed' ? 'error' : 'primary'}
                variant="outlined"
              />
            )}
          </Box>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchJobDetails} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {job && (
          <>
            {/* Job Information */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandIcon />}>
                <Typography variant="h6">Job Information</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Job ID</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {job.id}
                      <Tooltip title="Copy Job ID">
                        <IconButton size="small" onClick={() => copyToClipboard(job.id.toString())}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Platform</Typography>
                    <Typography variant="body1">{job.platform_name || 'Unknown'}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Brand</Typography>
                    <Typography variant="body1">{job.brand_name || 'Unknown'}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Sync Type</Typography>
                    <Typography variant="body1">{job.sync_type || 'manual'}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Date Range</Typography>
                    <Typography variant="body1">
                      {job.date_range_start && job.date_range_end
                        ? `${job.date_range_start} to ${job.date_range_end}`
                        : 'Not specified'
                      }
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Priority</Typography>
                    <Typography variant="body1">{job.priority || 'N/A'}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                    <Typography variant="body1">
                      {job.created_at ? new Date(job.created_at).toLocaleString() : 'N/A'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Duration</Typography>
                    <Typography variant="body1">
                      {formatDuration(job.started_at, job.completed_at)}
                    </Typography>
                  </Box>

                  {job.progress_percentage !== undefined && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Progress
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={job.progress_percentage}
                          sx={{ flex: 1, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="body2" fontWeight={500}>
                          {job.progress_percentage}%
                        </Typography>
                      </Box>
                      {job.current_step && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {job.current_step}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {job.error_message && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="subtitle2" color="error" gutterBottom>
                        Error Message
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: 'error.light', color: 'error.dark' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {job.error_message}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Execution Logs */}
            <Accordion sx={{ mt: 1 }}>
              <AccordionSummary expandIcon={<ExpandIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mr: 1 }}>
                  <Typography variant="h6">
                    Execution Logs ({logs.length})
                  </Typography>
                  {logs.length > 0 && (
                    <Tooltip title="Download Logs">
                      <IconButton size="small" onClick={downloadLogs}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {logs.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width="150px">Timestamp</TableCell>
                          <TableCell width="80px">Level</TableCell>
                          <TableCell>Message</TableCell>
                          <TableCell width="100px">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {logs.map((log, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {new Date(log.timestamp).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={getLogLevelIcon(log.log_level)}
                                label={log.log_level}
                                size="small"
                                color={getLogLevelColor(log.log_level)}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ 
                                fontFamily: log.details ? 'inherit' : 'monospace',
                                wordBreak: 'break-word'
                              }}>
                                {log.message}
                              </Typography>
                              {log.details && (
                                <Typography variant="caption" color="text.secondary" sx={{ 
                                  fontFamily: 'monospace',
                                  display: 'block',
                                  mt: 0.5,
                                  pl: 1,
                                  borderLeft: 2,
                                  borderColor: 'divider'
                                }}>
                                  {log.details}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Copy Message">
                                <IconButton 
                                  size="small" 
                                  onClick={() => copyToClipboard(`[${log.timestamp}] ${log.log_level}: ${log.message}${log.details ? '\n' + log.details : ''}`)}
                                >
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">
                    No logs available for this job yet.
                  </Alert>
                )}
              </AccordionDetails>
            </Accordion>
          </>
        )}

        {loading && job && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Updating job details...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {job?.status === 'queued' && (
          <Button 
            color="error" 
            onClick={() => handleJobAction('cancel')}
            disabled={loading}
          >
            Cancel Job
          </Button>
        )}
        
        {job?.status === 'failed' && (
          <Button 
            color="primary"
            variant="contained"
            onClick={() => handleJobAction('retry')}
            disabled={loading}
          >
            Retry Job
          </Button>
        )}
        
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobDetailsModal;
